import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { customerService } from '../../services/api';
import { canLookupPostalCode } from '../../utils/formatters';

/**
 * Postal code autofill for the address forms. Call `lookup(country, code)`
 * from the postal code field's onChange (user input only — loading a saved
 * record never triggers it). When the code is complete for a country with a
 * backend adapter (BR), it asks `/postal-codes/lookup/` and calls
 * `onFound(address)`; responses for a code that changed meanwhile are
 * dropped. Results are shared through react-query (`['postal-code', ...]`).
 *
 * `status`: 'idle' | 'loading' | 'notFound' | 'unavailable'.
 */
export default function usePostalCodeLookup({ onFound }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('idle');
  const latest = useRef('');
  const onFoundRef = useRef(onFound);
  onFoundRef.current = onFound;

  const lookup = useCallback(
    async (country, code) => {
      const key = `${country}:${code}`;
      latest.current = key;
      if (!canLookupPostalCode(country, code)) {
        setStatus('idle');
        return;
      }
      setStatus('loading');
      try {
        const address = await queryClient.fetchQuery({
          queryKey: ['postal-code', country, code],
          queryFn: () => customerService.lookupPostalCode({ country, code }).then((res) => res.data),
          staleTime: Infinity,
          retry: false,
        });
        if (latest.current !== key) return;
        setStatus('idle');
        onFoundRef.current(address);
      } catch (error) {
        if (latest.current !== key) return;
        setStatus(error?.response?.status === 404 ? 'notFound' : 'unavailable');
      }
    },
    [queryClient],
  );

  const reset = useCallback(() => {
    latest.current = '';
    setStatus('idle');
  }, []);

  return { status, lookup, reset };
}
